import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { IGuiaContenido, IGuiaTema, IGuiaTipoRecurso } from 'src/app/shared/interfaces/IGuiaUsuario';
import { ResponseModel } from 'src/app/shared/models/ResponseModel';

/**
 * Guía de usuario: consulta de los temas (texto, PDF y video) que alimentan el panel
 * lateral de ayuda del header. Los archivos los sirve la propia API desde su directorio
 * de red, bajo el prefijo `/archivos-guia`.
 */
@Injectable({ providedIn: 'root' })
export class GuiaUsuarioService {
    private readonly apiUrl = environment.backUrlApi + 'GuiaUsuario';
    private readonly http = inject(HttpClient);

    /**
     * Temas de la guía del portal, cada uno con su texto y sus archivos. `busqueda` filtra
     * en el servidor y devuelve el tema completo cuando alguno de sus recursos coincide.
     */
    getGuias(idAplicacion: number, busqueda?: string): Observable<ResponseModel<IGuiaTema[]>> {
        let params = new HttpParams().set('idAplicacion', idAplicacion);

        const termino = busqueda?.trim();
        if (termino) params = params.set('busqueda', termino);

        return this.http.get<ResponseModel<IGuiaTema[]>>(`${this.apiUrl}/guias`, { params });
    }

    /**
     * Contenido del archivo de un recurso de texto. Se pide al expandir el acordeón, no
     * en el listado: así el panel abre rápido aunque haya muchos temas.
     */
    getContenido(idRecurso: number): Observable<ResponseModel<IGuiaContenido>> {
        return this.http.get<ResponseModel<IGuiaContenido>>(`${this.apiUrl}/recursos/${idRecurso}/contenido`);
    }

    /** Catálogo de tipos de recurso, para filtros e íconos. */
    getTipos(): Observable<ResponseModel<IGuiaTipoRecurso[]>> {
        return this.http.get<ResponseModel<IGuiaTipoRecurso[]>>(`${this.apiUrl}/tipos`);
    }

    /**
     * URL absoluta del archivo. La API devuelve la ruta servida (`/archivos-guia/...`),
     * que cuelga del host de la API y no del front — de ahí el prefijo.
     *
     * Se codifica porque las carpetas del directorio de red se nombran en lenguaje
     * natural (`Actualizacion de datos`) y esos espacios no son válidos en una URL.
     * `encodeURI` respeta los `/` de la ruta; se omite si el valor ya viene codificado
     * para no escapar dos veces el `%`.
     */
    urlAbsoluta(url: string): string {
        if (!url) return '';

        const absoluta = /^https?:\/\//i.test(url) ? url : `${environment.homeImagesUrl}${url.startsWith('/') ? '' : '/'}${url}`;

        return /%[0-9a-f]{2}/i.test(absoluta) ? absoluta : encodeURI(absoluta);
    }
}
